import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { inventoryService } from '../../services/api';

const InventoryTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
                                             type: '',
                                             dateRange: '30',
                                             search: '',
                                         });

    useEffect(() => {
        const loadTransactions = async () => {
            try {
                setLoading(true);
                const data = await inventoryService.getTransactions(filter);
                setTransactions(data);
            } catch (error) {
                console.error("Error fetching transactions:", error);
                // Show error toast
            } finally {
                setLoading(false);
            }
        };

        loadTransactions();
    }, [filter]);

    const handleFilterChange = (key, value) => {
        setFilter(prev => ({ ...prev, [key]: value }));
    };

    const renderTransactionType = (type) => {
        const classes = {
            RECEIVE: 'bg-green-100 text-green-800',
            ISSUE: 'bg-red-100 text-red-800',
            ADJUST: 'bg-yellow-100 text-yellow-800',
            TRANSFER: 'bg-blue-100 text-blue-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
        );
    };

    const renderQuantity = (quantity, type) => {
        const classes = type === 'ISSUE' ? 'text-red-500' : type === 'RECEIVE' ? 'text-green-500' : '';
        const prefix = type === 'ISSUE' ? '-' : '+';

        return (
            <span className={classes}>
        {prefix}{quantity}
      </span>
        );
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Inventory Transactions</h2>
                </div>
                <div className="p-6">
                    <div className="flex flex-wrap gap-4 mb-6">
                        <div className="w-full md:w-64">
                            <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Search by part or reference..."
                                value={filter.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                        <div className="w-full md:w-48">
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filter.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                            >
                                <option value="">All Types</option>
                                <option value="RECEIVE">Receive</option>
                                <option value="ISSUE">Issue</option>
                                <option value="ADJUST">Adjustment</option>
                                <option value="TRANSFER">Transfer</option>
                            </select>
                        </div>
                        <div className="w-full md:w-48">
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filter.dateRange}
                                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                            >
                                <option value="7">Last 7 Days</option>
                                <option value="30">Last 30 Days</option>
                                <option value="90">Last 90 Days</option>
                                <option value="365">Last Year</option>
                            </select>
                        </div>
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => setFilter({ type: '', dateRange: '30', search: '' })}
                        >
                            Reset Filters
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No transactions found
                        </div>
                    ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Type</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Location</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Location</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {transactions.map((transaction) => (
                                        <tr key={transaction.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {format(new Date(transaction.date), 'MM/dd/yyyy')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renderTransactionType(transaction.type)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.reference}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.part.partNumber}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.part.description}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {renderQuantity(transaction.quantity, transaction.type)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.locationFrom}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.locationTo}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {transaction.createdBy}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default InventoryTransactions;
